/**
 * ContactShadowsConfig — configuration for the screen-space contact shadows pass.
 *
 * Contact shadows are cheap screen-space shadows generated by ray-marching the
 * depth buffer along the primary directional light direction. They fill the gap
 * where CSM resolution is insufficient (e.g. small contact points, thin geometry
 * touching a surface) without requiring additional shadow maps.
 *
 * Algorithm reference:
 *   - Bungie "Screen-Space Contact Shadows" (Destiny)
 *   - Unreal Engine 5 Contact Shadows
 *   - Horizon Zero Dawn GDC: screen-space shadows for vegetation contacts
 */

/** Full contact-shadows configuration (all optional — defaults provided). */
export interface ContactShadowsConfig {
  /**
   * Number of ray-march steps per pixel.
   * Higher = more accurate at the cost of GPU fill rate.
   * Range: 8..32. Default: 16.
   */
  sampleCount: number;

  /**
   * Maximum ray-march distance in view-space metres.
   * Shadows beyond this distance from the surface are not cast.
   * Default: 0.5 m — suitable for character-on-floor contacts.
   */
  maxDistance: number;

  /**
   * Depth-comparison thickness tolerance in view-space metres.
   * When the marched position is BEHIND the depth buffer by more than this
   * amount the sample is considered in shadow.  Too small → false positives
   * on smooth surfaces; too large → shadows leak behind geometry.
   * Default: 0.015 m.
   */
  thickness: number;

  /**
   * Shadow strength (opacity) in [0, 1].
   * 0 = no shadow, 1 = full darkening of the scene colour.
   * Default: 0.5.
   */
  strength: number;

  /**
   * Apply jittered (dithered) sample offsets to hide banding artefacts.
   * Uses a blue-noise-like interleaved gradient noise pattern that pairs
   * well with temporal AA (TRAA) for free denoising.
   * Default: true.
   */
  noise: boolean;

  /**
   * Slope-bias multiplier applied to the first march step.
   * Prevents self-shadowing acne on slanted surfaces.
   * The actual bias is: slopeBias * stepSize * normalDotLight.
   * Default: 1.0 (no extra multiplier — the step size itself biases the first sample).
   */
  slopeBias: number;
}

/** Default config — sensible values for a physically-based day scene. */
export const DEFAULT_CONTACT_SHADOWS_CONFIG: ContactShadowsConfig = {
  sampleCount: 16,
  maxDistance: 0.5,
  thickness: 0.015,
  strength: 0.5,
  noise: true,
  slopeBias: 1.0,
};

/**
 * Merge partial user config with defaults.
 * All undefined fields fall back to DEFAULT_CONTACT_SHADOWS_CONFIG.
 */
export function resolveContactShadowsConfig(
  partial?: Partial<ContactShadowsConfig>,
): ContactShadowsConfig {
  return { ...DEFAULT_CONTACT_SHADOWS_CONFIG, ...partial };
}
